import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import { useAuth } from "@/lib/auth";
import { apiClient } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Briefcase, Sparkles, Bell, Mail, Trash2, X, Globe2 } from "lucide-react";
import JourneysPanel from "@/components/JourneysPanel";
import {
  pushSupported,
  subscribePush,
  unsubscribePush,
  getCurrentPushSubscription,
} from "@/lib/pwa";
import { toast } from "sonner";

const TIER_LABEL = {
  free: { name: "Free", color: "var(--ink-2)" },
  mastery: { name: "AI Mastery", color: "var(--primary,#2563EB)" },
  // Legacy — only displayed for grandfathered subscribers whose career/pro paid term is still active.
  career: { name: "AI Mastery", color: "var(--primary,#2563EB)" },
  pro: { name: "AI Mastery", color: "var(--primary,#2563EB)" },
};

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className="inline-flex items-center w-12 h-7 rounded-full transition-colors relative"
      style={{
        background: checked ? "var(--emerald, #10B981)" : "#D1D5DB",
        opacity: disabled ? 0.5 : 1,
      }}
      data-testid={testid}
    >
      <span
        className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all"
        style={{ left: checked ? "calc(100% - 1.625rem)" : "0.125rem" }}
      />
    </button>
  );
}

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [emailOpt, setEmailOpt] = useState(user?.email_opt_in !== false);
  const [pushOpt, setPushOpt] = useState(!!user?.push_opt_in);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPush, setSavingPush] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const pushOk = pushSupported();

  const openBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const r = await apiClient.post("/billing/portal", {
        return_url: window.location.origin + "/settings",
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No portal URL");
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Could not open billing portal.";
      toast.error(typeof msg === "string" ? msg : "Could not open billing portal.");
      setOpeningPortal(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    setEmailOpt(user.email_opt_in !== false);
    setPushOpt(!!user.push_opt_in);
  }, [user]);

  if (!user) return null;
  const tier = user.tier || "free";
  const tierInfo = TIER_LABEL[tier] || TIER_LABEL.free;
  const renewalDate = user.mastery_until || user.pro_until || user.career_until;

  const toggleEmail = async (next) => {
    setSavingEmail(true);
    try {
      await apiClient.patch("/me/preferences", { email_opt_in: next });
      setEmailOpt(next);
      await refresh?.();
      toast.success(next ? "Email reminders on" : "Email reminders off");
    } catch (e) {
      toast.error("Couldn't save preference");
    } finally {
      setSavingEmail(false);
    }
  };

  const togglePush = async (next) => {
    setSavingPush(true);
    try {
      if (next) {
        await subscribePush();
        toast.success("Push notifications on");
      } else {
        await unsubscribePush();
        await apiClient.patch("/me/preferences", { push_opt_in: false });
        toast.success("Push notifications off");
      }
      setPushOpt(next);
      await refresh?.();
    } catch (e) {
      toast.error(e?.message || "Couldn't change push setting");
    } finally {
      setSavingPush(false);
    }
  };

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14 pb-28 md:pb-14">
        <span className="kicker">Account</span>
        <h1 className="font-display text-5xl mt-3 leading-tight tracking-tight">
          Settings
        </h1>

        <div className="brut p-7 mt-8">
          <div className="flex items-center gap-4">
            {user.picture && (
              <img
                src={user.picture}
                alt=""
                className="w-12 h-12 rounded-full"
              />
            )}
            <div>
              <div className="font-medium text-lg">{user.name}</div>
              <div className="text-xs text-[var(--ink-2)] mt-0.5">
                {user.email}
              </div>
            </div>
          </div>
        </div>

        <div className="brut p-7 mt-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <span className="kicker">Current plan</span>
              <h3 className="font-display text-3xl mt-1.5 leading-tight">
                {tierInfo.name}
                {tier !== "free" && (
                  <span
                    className="ml-2 inline-block w-2 h-2 rounded-full align-middle"
                    style={{ background: tierInfo.color }}
                  />
                )}
              </h3>
              {tier === "career" && user.career_until && (
                <p className="mt-2 text-sm text-[var(--ink-2)]">
                  Renews until{" "}
                  {new Date(user.career_until).toLocaleDateString()}
                </p>
              )}
              {tier === "pro" && user.pro_until && (
                <p className="mt-2 text-sm text-[var(--ink-2)]">
                  Renews until {new Date(user.pro_until).toLocaleDateString()}
                </p>
              )}
              {tier === "mastery" && renewalDate && (
                <p className="mt-2 text-sm text-[var(--ink-2)]">
                  Renews until {new Date(renewalDate).toLocaleDateString()}
                </p>
              )}
              {tier === "free" && (
                <p className="mt-2 text-sm text-[var(--ink-2)]">
                  Continue your journey to unlock unlimited daily challenges and the full archive.
                </p>
              )}
              {user.has_subscription && user.subscription_cancel_at_period_end && (
                <p
                  className="mt-2 text-sm text-[var(--accent,#FF5C2A)]"
                  data-testid="settings-cancel-notice"
                >
                  Cancellation scheduled — your plan ends at the period close. Re-open the portal to resume.
                </p>
              )}
              {user.subscription_status === "paused" && (
                <p
                  className="mt-2 text-sm text-[var(--accent,#FF5C2A)]"
                  data-testid="settings-paused-notice"
                >
                  Subscription paused — access is on hold. Open the portal to resume any time.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 min-w-[12rem]">
              {tier === "free" && (
                <Link
                  to="/pricing"
                  className="btn-accent justify-center inline-flex items-center min-h-[44px]"
                  data-testid="settings-upgrade-button"
                >
                  <Sparkles size={14} /> Continue Your Journey
                </Link>
              )}
              {tier !== "free" && user.has_subscription && (
                <button
                  type="button"
                  onClick={openBillingPortal}
                  disabled={openingPortal}
                  className="btn-primary justify-center disabled:opacity-60"
                  data-testid="settings-manage-subscription"
                >
                  {openingPortal ? "Opening…" : "Manage subscription"}
                </button>
              )}
              {tier !== "free" && !user.has_subscription && (
                <Link
                  to="/pricing"
                  className="btn-ghost justify-center"
                  data-testid="settings-renew-button"
                >
                  Renew plan
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* My Journeys + Identity Vault (Phase 2 foundation) */}
        <JourneysPanel />

        {/* Notifications */}
        <div className="brut p-7 mt-5" data-testid="settings-notifications">
          <span className="kicker">Notifications</span>
          <h3 className="font-display text-2xl mt-1.5 leading-tight">
            Stay on streak
          </h3>
          <p className="text-sm text-[var(--ink-2)] mt-1 leading-relaxed">
            Three gentle nudges per day in your local time — morning challenge, lunchtime lesson, evening streak save. Unsubscribe any time.
          </p>

          <div className="mt-5 flex items-center justify-between gap-4 py-3 border-t border-[var(--line)]">
            <div className="flex items-start gap-3 min-w-0">
              <Mail size={16} className="mt-0.5 text-[var(--accent)] flex-shrink-0" />
              <div>
                <div className="font-medium text-[var(--ink)] text-sm">Email reminders</div>
                <div className="text-xs text-[var(--ink-3)] mt-0.5">Belt progress + streak nudges to {user.email}</div>
              </div>
            </div>
            <Toggle
              checked={emailOpt}
              onChange={toggleEmail}
              disabled={savingEmail}
              testid="settings-email-toggle"
            />
          </div>

          <div className="flex items-center justify-between gap-4 py-3 border-t border-[var(--line)]">
            <div className="flex items-start gap-3 min-w-0">
              <Bell size={16} className="mt-0.5 text-[var(--emerald)] flex-shrink-0" />
              <div>
                <div className="font-medium text-[var(--ink)] text-sm">Push notifications</div>
                <div className="text-xs text-[var(--ink-3)] mt-0.5">
                  {pushOk
                    ? "7–9 AM challenge · 11 AM–1 PM lesson · 7–9 PM streak · auto-tuned to your timezone"
                    : "Not supported on this browser"}
                </div>
              </div>
            </div>
            <Toggle
              checked={pushOpt}
              onChange={togglePush}
              disabled={!pushOk || savingPush}
              testid="settings-push-toggle"
            />
          </div>
        </div>

        <Link
          to="/refer"
          className="brut p-5 mt-5 flex items-center justify-between gap-4 hover:border-[var(--ink)] transition-colors group"
          data-testid="settings-refer-link"
        >
          <div>
            <span className="kicker">Referrals &amp; rewards</span>
            <div className="font-display text-2xl mt-1 leading-tight">
              Invite a friend and you both win
            </div>
            <div className="text-sm text-[var(--ink-2)] mt-1">
              You both unlock <strong>90% off Monthly</strong> + <strong>30% off Annual</strong> (30-day window). Bonus belt progress per friend who joins.
            </div>
          </div>
          <span className="text-[var(--ink-2)] group-hover:text-[var(--ink)] transition-colors">→</span>
        </Link>

        {/* Region & locale */}
        <LocalePanel user={user} onSaved={refresh} />

        <button
          onClick={async () => {
            await logout();
            navigate("/");
          }}
          className="btn-ghost mt-7"
          data-testid="settings-logout-button"
        >
          Log out
        </button>

        {/* Danger zone */}
        <DangerZone email={user.email} onDeleted={() => navigate("/", { replace: true })} />
      </main>
    </div>
  );
}

function DangerZone({ email, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await apiClient.delete("/auth/me", { data: { confirm_email: confirmEmail } });
      toast.success("Your account is deleted. We're sorry to see you go.");
      onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete your account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-12 pt-4 text-center" data-testid="settings-danger-zone">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink-2)] underline-offset-2 hover:underline transition-colors"
          data-testid="settings-delete-account-open"
        >
          Delete account
        </button>
      ) : (
        <form
          onSubmit={submit}
          className="brut p-5 max-w-md mx-auto text-left space-y-3"
          style={{ background: "rgba(220,38,38,0.03)", borderColor: "rgba(220,38,38,0.20)" }}
          data-testid="settings-delete-account-form"
        >
          <div className="font-medium text-sm text-[var(--ink)]">Delete your account</div>
          <p className="text-xs text-[var(--ink-2)] leading-relaxed">
            This permanently removes your profile, progress, streak, and push subscriptions.
            Payment records are anonymized but retained for accounting. This can't be undone.
          </p>
          <div className="text-xs text-[var(--ink-2)] leading-relaxed">
            Type <strong>{email}</strong> to confirm:
          </div>
          <input
            type="email"
            className="brut-input"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder={email}
            autoComplete="off"
            data-testid="settings-delete-account-confirm-email"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy || confirmEmail.trim().toLowerCase() !== (email || "").toLowerCase()}
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-red-600 text-white rounded-full px-3 py-2 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="settings-delete-account-confirm"
            >
              {busy ? <span className="spinner" /> : <><Trash2 size={12} /> Permanently delete</>}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setConfirmEmail(""); }}
              className="text-xs text-[var(--ink-3)] hover:text-[var(--ink-2)] inline-flex items-center gap-1"
              data-testid="settings-delete-account-cancel"
            >
              <X size={12} /> Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}


// ── Locale / Region panel ────────────────────────────────────────────────
const COUNTRY_OPTIONS = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "New Zealand" },
  { code: "IE", name: "Ireland" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" },
  { code: "IN", name: "India" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "SG", name: "Singapore" },
  { code: "JP", name: "Japan" },
  { code: "PH", name: "Philippines" },
  { code: "BR", name: "Brazil" },
  { code: "MX", name: "Mexico" },
];
const CURRENCY_OPTIONS = ["USD", "GBP", "EUR", "CAD", "AUD", "INR", "AED", "SGD", "JPY", "PHP"];
const LANGUAGE_OPTIONS = [
  { code: "en", name: "English" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "de", name: "Deutsch" },
  { code: "it", name: "Italiano" },
  { code: "pt", name: "Português" },
  { code: "ja", name: "日本語" },
  { code: "hi", name: "हिन्दी" },
];

function LocalePanel({ user, onSaved }) {
  const [country, setCountry] = useState(user?.country || "");
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [language, setLanguage] = useState(user?.language_code || "en");
  const [timezone, setTimezone] = useState(user?.timezone || "");
  const [saving, setSaving] = useState(false);

  const detected = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return null;
    }
  })();

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      if (country) patch.country = country;
      if (currency) patch.currency = currency;
      if (language) patch.language_code = language;
      if (timezone) patch.timezone = timezone;
      await apiClient.post("/profile/locale", patch);
      toast.success("Region settings saved.");
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save region settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="brut p-7 mt-5" data-testid="settings-locale">
      <div className="flex items-center gap-2.5">
        <Globe2 size={18} className="text-[var(--ink-3)]" />
        <span className="kicker">Region &amp; language</span>
      </div>
      <h3 className="font-display text-2xl mt-1.5 leading-tight">Where you are</h3>
      <p className="text-sm text-[var(--ink-2)] mt-1 leading-relaxed">
        Your country, currency, and timezone shape pricing, notifications, and lesson timing.
        We auto-detect from your browser — you can override below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
        <label className="block">
          <span className="text-xs text-[var(--ink-3)] uppercase tracking-wider">Country</span>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="mt-1.5 w-full brut p-3 text-sm bg-white"
            data-testid="settings-country-select"
          >
            <option value="">— Auto-detect —</option>
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-[var(--ink-3)] uppercase tracking-wider">Currency</span>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="mt-1.5 w-full brut p-3 text-sm bg-white"
            data-testid="settings-currency-select"
          >
            {CURRENCY_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-[var(--ink-3)] uppercase tracking-wider">Language</span>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="mt-1.5 w-full brut p-3 text-sm bg-white"
            data-testid="settings-language-select"
          >
            {LANGUAGE_OPTIONS.map((l) => (
              <option key={l.code} value={l.code}>{l.name}</option>
            ))}
          </select>
          <span className="text-[10px] text-[var(--ink-3)] mt-1 block">
            English only for now — more coming soon.
          </span>
        </label>

        <label className="block">
          <span className="text-xs text-[var(--ink-3)] uppercase tracking-wider">Timezone (IANA)</span>
          <input
            type="text"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder={detected || "America/New_York"}
            className="mt-1.5 w-full brut p-3 text-sm bg-white font-mono"
            data-testid="settings-timezone-input"
          />
          {detected && detected !== timezone && (
            <button
              type="button"
              onClick={() => setTimezone(detected)}
              className="text-[10px] text-[var(--primary,#2563EB)] mt-1 hover:underline"
              data-testid="settings-use-detected-tz"
            >
              Use detected: {detected}
            </button>
          )}
        </label>
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="btn-primary mt-5"
        style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}
        data-testid="settings-locale-save"
      >
        {saving ? "Saving…" : "Save region settings"}
      </button>
    </div>
  );
}
